import { useState, useEffect } from "react";
import { DatePicker } from "@mui/x-date-pickers";
import useForm from "../common/AsyncFormBox/useForm";
import { getInitialValue } from "./internal/dateInputUtils";

/**
 * DateInput component for selecting a date
 * @param {Object} props
 * @param {string} props.name - Name for the form field
 * @param {string} [props.label] - Label for the input
 * @param {Function} [props.onChange] - Callback when value changes, receives dayjs object
 * @param {dayjs|string} [props.defaultValue] - Default value as dayjs object or ISO string
 * @param {dayjs|string} [props.value] - Controlled value (overrides defaultValue)
 * @param {boolean} [props.required] - Whether the field is required
 * @param {Array} [props.views] - Views to show in the picker
 * @param {string} [props.format] - Date format (null to use default)
 * @param {dayjs} [props.maxDate] - Maximum date
 * @param {dayjs} [props.openTo] - Initial view to open
 * @param {boolean} [props.readOnly] - Whether field is read-only
 * @param {boolean} [props.disabled] - Whether field is disabled
 * @param {string} [props.size] - Size of the input field
 * @param {string} [props.error] - Error message to display
 */
function DateInput({
  name,
  label,
  onChange,
  defaultValue,
  value: controlledValue,
  required,
  views,
  format,
  maxDate,
  openTo,
  readOnly,
  disabled,
  size,
  error,
}) {
  const form = useForm();
  const [value, setValue] = useState(() => {
    if (controlledValue !== undefined) {
      return controlledValue || null;
    }
    return getInitialValue(defaultValue, name, form);
  });

  // Sync with controlled value if provided
  useEffect(() => {
    if (controlledValue !== undefined) {
      setValue(controlledValue || null);
      if (form && name) {
        form.setValue(name, controlledValue || null);
      }
    }
  }, [controlledValue, name, form]);

  function handleOnChange(newValue) {
    if (readOnly) return;
    setValue(newValue);
    form?.setValue(name, newValue);
    onChange && onChange(newValue);
  }

  return (
    <DatePicker
      format={format === null ? undefined : "DD.MM.YYYY"}
      slotProps={{
        textField: {
          fullWidth: true,
          name: name,
          required: required,
          InputProps: { readOnly },
          variant: readOnly ? "filled" : "outlined",
          size: size,
          error: !!error,
          helperText: error,
        },
      }}
      label={label}
      value={value}
      onChange={handleOnChange}
      views={views}
      maxDate={maxDate}
      openTo={openTo}
      disabled={disabled}
    />
  );
}

export default DateInput;
